import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import useSEO from '@/hooks/useSEO';
import Breadcrumb from '@/components/base/Breadcrumb';

const AboutPage = () => {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '红桃视频',
    url: 'https://hongtao.video',
    description: '红桃视频免费在线观看平台 - 海量高清影视资源',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@hongtao.video',
      telephone: '+86-400-888-0000',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '红桃视频是否需要注册才能观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！红桃视频完全免费，无需注册即可观看所有影片。我们致力于为用户提供最便捷的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '红桃视频支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '红桃视频支持手机、平板、电脑等多种设备观看。无论是iOS、Android还是Windows、Mac系统，都可以流畅播放。',
        },
      },
      {
        '@type': 'Question',
        name: '红桃视频的影片画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '红桃视频所有影片均提供高清画质，部分影片支持超清1080P和4K画质，给您最佳的视觉体验。',
        },
      },
      {
        '@type': 'Question',
        name: '红桃视频有广告吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '红桃视频坚持无广告观影理念，播放过程中不会插入任何形式的广告，让您畅享纯净观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '红桃视频的资源更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '红桃视频每日更新最新影视资源，热门剧集同步更新，确保用户第一时间看到最新内容。',
        },
      },
    ],
  };

  useSEO({
    title: '关于红桃视频 - 免费高清在线观看平台',
    description: '红桃视频是一个致力于为用户提供免费高清影视在线观看服务的平台。了解红桃视频的品牌故事、用户口碑和联系方式。',
    keywords: '红桃视频,关于红桃视频,免费视频平台,高清影视,在线观看平台',
    canonicalUrl: 'https://hongtao.video/about',
    ogTitle: '关于红桃视频 - 免费高清在线观看平台',
    ogDescription: '了解红桃视频的品牌故事、用户口碑和联系方式。海量高清影视资源免费在线观看。',
    ogUrl: 'https://hongtao.video/about',
    structuredData: [organizationSchema, faqSchema],
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const formActionUrl = form.getAttribute('action') || '';
      const response = await fetch(formActionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), to: '/' },
            { label: t('nav.about') },
          ]}
        />
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
        <div className="text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
              <i className="ri-play-circle-fill text-2xl text-background-50"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('about.title')}
            </h1>
          </div>
          <p className="text-foreground-600 max-w-xl mx-auto leading-relaxed">
            {t('site.slogan')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-14">
          <div className="bg-background-100/70 rounded-xl p-6 hover:scale-[1.02] transition-transform duration-300">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
              <i className="ri-eye-line text-xl text-primary-600"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('about.intro')}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {t('about.intro.content')}
            </p>
          </div>

          <div className="bg-background-100/70 rounded-xl p-6 hover:scale-[1.02] transition-transform duration-300">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
              <i className="ri-heart-line text-xl text-accent-600"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('about.mission')}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {t('about.mission.content')}
            </p>
          </div>

          <div className="bg-background-100/70 rounded-xl p-6 hover:scale-[1.02] transition-transform duration-300">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 mb-4">
              <i className="ri-thumb-up-line text-xl text-secondary-600"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('about.reputation')}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {t('about.reputation.content')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-14">
          <div className="text-center p-6 rounded-xl bg-primary-100/70">
            <div className="text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>5000万+</div>
            <p className="text-sm text-foreground-600 mt-1">累计用户</p>
          </div>
          <div className="text-center p-6 rounded-xl bg-accent-100/70">
            <div className="text-3xl font-bold text-accent-600" style={{ fontFamily: 'var(--font-heading)' }}>300万+</div>
            <p className="text-sm text-foreground-600 mt-1">日活跃用户</p>
          </div>
          <div className="text-center p-6 rounded-xl bg-secondary-100/70">
            <div className="text-3xl font-bold text-secondary-600" style={{ fontFamily: 'var(--font-heading)' }}>10万+</div>
            <p className="text-sm text-foreground-600 mt-1">影视资源</p>
          </div>
          <div className="text-center p-6 rounded-xl bg-primary-100/70">
            <div className="text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>99.9%</div>
            <p className="text-sm text-foreground-600 mt-1">用户好评率</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-foreground-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              <div className="w-1 h-5 rounded-full bg-primary-500" />
              {t('about.contact')}
            </h2>
            <div className="space-y-3 bg-background-100/70 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{t('about.contact.email')}</p>
                  <p className="text-sm font-medium text-foreground-900">service@hongtao.video</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{t('about.contact.phone')}</p>
                  <p className="text-sm font-medium text-foreground-900">400-888-0000</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{t('about.contact.address')}</p>
                  <p className="text-sm font-medium text-foreground-900">北京市朝阳区建国路88号红桃大厦</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              <div className="w-1 h-5 rounded-full bg-accent-500" />
              给我们留言
            </h2>
            <form
              ref={formRef}
              action="https://readdy.ai/api/form/d9qt31vnahb7382ovfbg"
              method="POST"
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="bg-background-100/70 rounded-xl p-6 space-y-4"
            >
              <div>
                <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="about-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-300"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="about-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-300"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="about-message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-300 resize-none"
                  placeholder="请输入您的留言内容..."
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>
              <div className="antispam-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>
              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formMessage}</p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="text-xl font-bold text-foreground-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <div className="w-1 h-5 rounded-full bg-primary-500" />
            常见问题
          </h2>
          <div className="space-y-3">
            {[
              { q: '红桃视频是否需要注册才能观看？', a: '不需要！红桃视频完全免费，无需注册即可观看所有影片。我们致力于为用户提供最便捷的观影体验。' },
              { q: '红桃视频支持哪些设备观看？', a: '红桃视频支持手机、平板、电脑等多种设备观看。无论是iOS、Android还是Windows、Mac系统，都可以流畅播放。' },
              { q: '红桃视频的影片画质如何？', a: '红桃视频所有影片均提供高清画质，部分影片支持超清1080P和4K画质，给您最佳的视觉体验。' },
              { q: '红桃视频有广告吗？', a: '红桃视频坚持无广告观影理念，播放过程中不会插入任何形式的广告，让您畅享纯净观影体验。' },
              { q: '红桃视频的资源更新频率如何？', a: '红桃视频每日更新最新影视资源，热门剧集同步更新，确保用户第一时间看到最新内容。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-100/70 rounded-xl overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 list-none">
                  <span>{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-300"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;